import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FolderOpen, Link, Upload, Loader2, FileText, Download,
  Check, X, ExternalLink, RefreshCw, Trash2, Eye, Cloud
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleDriveManager = ({ token, professions }) => {
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [imports, setImports] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [newImport, setNewImport] = useState({
    drive_url: '',
    profession_id: '',
    content_type: 'material',
    title: ''
  });
  const [oauthConfig, setOauthConfig] = useState({ configured: false });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusRes, importsRes, materialsRes, configRes] = await Promise.all([
        axios.get(`${API}/drive/status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/drive/imports`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { imports: [] } })),
        axios.get(`${API}/drive/materials`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { materials: [] } })),
        axios.get(`${API}/drive/oauth/config`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { configured: false } }))
      ]);
      
      setDriveStatus(statusRes.data);
      setImports(importsRes.data.imports || []);
      setMaterials(materialsRes.data.materials || []);
      setOauthConfig(configRes.data);
    } catch (error) {
      console.error('Error loading drive data:', error);
    } finally {
      setLoading(false);
    }
  };

  const importPublicLink = async () => {
    if (!newImport.drive_url || !newImport.profession_id) {
      toast.error('Introduce la URL de Google Drive y selecciona una profesión');
      return;
    }

    setImporting(true);
    try {
      const res = await axios.post(`${API}/drive/import-public`, newImport, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setNewImport({ drive_url: '', profession_id: '', content_type: 'material', title: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importando archivo');
    } finally {
      setImporting(false);
    }
  };

  const connectDrive = async () => {
    try {
      const res = await axios.get(`${API}/drive/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error conectando con Google Drive');
    }
  };

  const disconnectDrive = async () => {
    try {
      await axios.delete(`${API}/drive/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Google Drive desconectado');
      loadData();
    } catch (error) {
      toast.error('Error desconectando');
    }
  };

  const publishMaterial = async (materialId) => {
    try {
      await axios.post(`${API}/drive/materials/${materialId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material publicado');
      loadData();
    } catch (error) {
      toast.error('Error publicando material');
    }
  };

  const deleteMaterial = async (materialId) => {
    if (!window.confirm('¿Eliminar este material?')) return;
    try {
      await axios.delete(`${API}/drive/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material eliminado');
      loadData();
    } catch (error) {
      toast.error('Error eliminando material');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Public Links */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Link className="w-8 h-8" />
            <div>
              <h3 className="font-semibold">Enlaces Públicos</h3>
              <p className="text-sm opacity-90">Pega cualquier enlace público de Google Drive</p>
            </div>
          </div>
          <p className="text-3xl font-bold">{imports.length}</p>
          <p className="text-sm opacity-75">Importaciones realizadas</p>
        </div>

        {/* OAuth Connection */}
        <div className={`rounded-xl p-6 text-white ${driveStatus.connected ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-slate-500 to-slate-600'}`}>
          <div className="flex items-center gap-3 mb-4">
            <Cloud className="w-8 h-8" />
            <div>
              <h3 className="font-semibold">Cuenta Conectada</h3>
              <p className="text-sm opacity-90">
                {driveStatus.connected ? 'Acceso a archivos privados' : 'Conecta tu Google Drive'}
              </p>
            </div>
          </div>
          {driveStatus.connected ? (
            <>
              <p className="text-lg font-medium mb-2">✅ Conectado</p>
              <Button variant="secondary" size="sm" onClick={disconnectDrive}>
                Desconectar
              </Button>
            </>
          ) : (
            <Button 
              variant="secondary" 
              onClick={connectDrive}
              disabled={!oauthConfig.configured}
            >
              {oauthConfig.configured ? 'Conectar Google Drive' : 'OAuth no configurado'}
            </Button>
          )}
        </div>
      </div>

      {/* Import Public Link */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-blue-600" />
          Importar desde Enlace Público
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-600">URL de Google Drive</label>
            <Input
              value={newImport.drive_url}
              onChange={(e) => setNewImport({ ...newImport, drive_url: e.target.value })}
              placeholder="https://drive.google.com/file/d/... o https://drive.google.com/open?id=..."
              className="mt-1"
            />
            <p className="text-xs text-slate-500 mt-1">Asegúrate de que el archivo es público (cualquier persona con el enlace)</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-600">Profesión*</label>
              <select
                value={newImport.profession_id}
                onChange={(e) => setNewImport({ ...newImport, profession_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg"
              >
                <option value="">Selecciona...</option>
                {professions?.map(p => (
                  <option key={p.profession_id} value={p.profession_id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600">Tipo de Contenido</label>
              <select
                value={newImport.content_type}
                onChange={(e) => setNewImport({ ...newImport, content_type: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg"
              >
                <option value="material">Material de estudio</option>
                <option value="temario">Temario oficial</option>
                <option value="preguntas">Preguntas de examen</option>
                <option value="legislacion">Legislación</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600">Título (opcional)</label>
              <Input
                value={newImport.title}
                onChange={(e) => setNewImport({ ...newImport, title: e.target.value })}
                placeholder="Nombre del archivo"
                className="mt-1"
              />
            </div>
          </div>

          <Button 
            onClick={importPublicLink} 
            disabled={importing || !newImport.drive_url || !newImport.profession_id}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importar Archivo
          </Button>
        </div>
      </div>

      {/* Recent Imports */}
      {imports.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Importaciones Recientes</h3>
            <Button variant="ghost" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="space-y-2">
            {imports.slice(0, 10).map(imp => (
              <div key={imp.import_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{imp.title || imp.filename || 'Archivo'}</p>
                    <p className="text-xs text-slate-500">{imp.drive_url?.substring(0, 50)}...</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    imp.status === 'completed' ? 'bg-green-100 text-green-700' :
                    imp.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {imp.status === 'completed' ? '✅ Completado' : 
                     imp.status === 'failed' ? '❌ Error' : '⏳ Procesando'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Materials List */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Materiales Importados</h3>
        
        {materials.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No hay materiales importados todavía</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-3 text-sm text-slate-600">Título</th>
                  <th className="text-left p-3 text-sm text-slate-600">Tipo</th>
                  <th className="text-left p-3 text-sm text-slate-600">Estado</th>
                  <th className="text-left p-3 text-sm text-slate-600">Fecha</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {materials.map(m => (
                  <tr key={m.material_id} className="border-b border-slate-100">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium">{m.title || m.filename}</span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-slate-600">{m.content_type}</td>
                    <td className="p-3">
                      {m.is_published ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Publicado</span>
                      ) : (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Pendiente</span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-slate-500">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <a href={`${API}/drive/file/${m.material_id}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                        {!m.is_published && (
                          <Button variant="ghost" size="sm" onClick={() => publishMaterial(m.material_id)}>
                            <Check className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deleteMaterial(m.material_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleDriveManager;
